import type { Filter } from "@/shared/components/ResourceView";
import type { AdminFeedbackFilters } from "@/shared/domain/admin/hooks/useAdminFeedback";
import type {
  FeedbackCategory,
  FeedbackSentiment,
} from "@/shared/domain/agentThread";

/**
 * Pull the committed admin-feedback filter set out of the ResourceView filter
 * chips. Each feedback field maps to a single equality predicate (the endpoint
 * has no OR/exclusion surface), so a field with anything other than exactly one
 * compatible chip resolves to "unset".
 *
 * The endpoint accepts only positive, single-value exact predicates, so an
 * unsupported chip resolves to "unset" rather than being coerced: an enum
 * field is honored only on ``EQUALS`` with one value, and ``thread_id`` only
 * on ``EQUALS``. ``NOT_EQUALS`` and multi-select ("is any of") have no
 * exclusion / OR surface here, so they drop to "unset" (the field is
 * unfiltered) rather than invert the result. This read-side guard keeps the
 * mapping correct even when such a chip arrives via a deep link.
 */
export function filtersToFeedbackFilters(
  filters: Filter[],
): AdminFeedbackFilters {
  const enumSingle = (field: string): string | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    return f?.type === "enum" &&
      f.comparator === "EQUALS" &&
      f.values.length === 1
      ? f.values[0]
      : undefined;
  };
  const stringSingle = (field: string): string | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    // Only ``EQUALS`` maps to the endpoint's exact-match param; any other
    // comparator (the filter UI is locked to ``EQUALS``, but guard anyway)
    // would otherwise be silently coerced to equality. Inequality (e.g.
    // "Session ≠ X") is a deliberate non-goal here — the feedback GET endpoint
    // has no exclusion surface; supporting it was deferred, not overlooked.
    return f?.type === "string" &&
      f.comparator === "EQUALS" &&
      f.values.length === 1
      ? f.values[0]
      : undefined;
  };
  const boolVal = (field: string): boolean | undefined => {
    const matches = filters.filter((f) => f.field === field);
    const f = matches.length === 1 ? matches[0] : undefined;
    if (f?.type !== "boolean") {
      return undefined;
    }
    // These boolean fields are EQUALS-only, so the chip's value is the answer.
    return f.values.length === 1 ? f.values[0] : undefined;
  };

  return {
    sentiment: enumSingle("sentiment") as FeedbackSentiment | undefined,
    category: enumSingle("category") as FeedbackCategory | undefined,
    orgId: enumSingle("org_id"),
    createdBy: enumSingle("created_by"),
    sessionId: stringSingle("thread_id"),
    resolved: boolVal("resolved"),
    hasNotes: boolVal("has_notes"),
    hasErrors: boolVal("has_errors"),
    isCustomer: boolVal("is_customer"),
  };
}
